import Vue from 'vue'
import Router from 'vue-router'
import config from '@/configs'
import { routeAllowsServices } from '@/utils/hasService'
import { shouldSwitchFrontend, switchFrontend } from '@/utils/frontendTarget'
import { canViewMenuModule, getSessionPermissions, isSuperAdmin } from '@/utils/profilePermissions'

import ServicesRoutes from './services.routes'
import PagesRoutes from './pages.routes'
import UsersRoutes from './users.routes'
import ProfilesRoutes from './profiles.routes'
import WhatsappRoutes from './whatsapp.routes'

Vue.use(Router)

export const routes = [
  {
    path: '/',
    redirect: '/auth/signin',
  },
  {
    path: '/two_factor_auth',
    name: 'two-factor-auth ',
    component: () =>
      import(
        /* webpackChunkName: "error-not-found" */ '@/pages/auth/TwoFactorAuth.vue'
      ),
    meta: {
      layout: 'error',
      requiresAuth: false,
    },
  },
  {
    path: '/dashboard/analytics',
    name: 'dashboard-analytics',
    component: () =>
      import(
        /* webpackChunkName: "dashboard" */ '@/pages/Dashboard/dashboard.vue'
      ),
    meta: { requiresAuth: true },
  },
  ...ServicesRoutes,
  ...PagesRoutes,
  ...UsersRoutes,
  ...ProfilesRoutes,
  ...WhatsappRoutes,
  {
    path: '/blank',
    name: 'blank',
    component: () =>
      import(/* webpackChunkName: "blank" */ '@/pages/BlankPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '*',
    name: 'error',
    component: () =>
      import(/* webpackChunkName: "error" */ '@/pages/error/NotFoundPage.vue'),
    meta: {
      layout: 'error',
      requiresAuth: false,
    },
  },
]

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL || '/',
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition

    return { x: 0, y: 0 }
  },
  routes
})

router.beforeEach((to, from, next) => {
  const isAuthenticated = $cookies.get('user') && $cookies.get('services') && $cookies.get('token')
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth === true)

  if (isAuthenticated && shouldSwitchFrontend($cookies.get('frontend_path'))) {
    switchFrontend($cookies.get('frontend_path'))
    return next(false)
  }

  if (to.name === 'auth-signin' && isAuthenticated) {
    return next({ name: 'dashboard-analytics' })
  }

  if (!isAuthenticated && requiresAuth) {
    return next({ name: 'auth-signin' })
  }

  if (isAuthenticated) {
    const services = $cookies.get('services') ?? []
    const deniedByServices = to.matched.some((record) => !routeAllowsServices(record.meta || {}, services))
    if (deniedByServices) {
      return next({ name: 'dashboard-analytics' })
    }

    const user = $cookies.get('user')
    const deniedByProfile = to.matched.some((record) => {
      const meta = record.meta || {}
      if (meta.requiresSuperAdmin && !isSuperAdmin(user)) {
        return true
      }
      return Boolean(meta.moduleKey) && !canViewMenuModule(user, getSessionPermissions(), meta.moduleKey)
    })
    if (deniedByProfile) {
      return next({ name: 'dashboard-analytics' })
    }
  }

  if (to.matched.some((record) => record.meta.requiresCatalogs) && !config.features.catalogsEnabled) {
    const senderId = to.params?.senderId
    if (senderId) {
      return next({ name: 'whatsapp-configuration', params: { senderId } })
    }
    return next({ name: 'dashboard-analytics' })
  }

  return next()
})

router.afterEach(() => {
})

export default router
